import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Eye, Users, TrendingUp, Calendar, Music, Share2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div
    data-testid={testid}
    className="border border-rule bg-paper p-6 flex flex-col gap-2"
  >
    <div className="flex items-center gap-2 text-ink-soft">
      <Icon size={16} />
      <p className="text-[11px] uppercase tracking-[0.2em]">{label}</p>
    </div>
    <p className="font-serif-display text-4xl text-ink leading-none">
      {Number(value || 0).toLocaleString()}
    </p>
    {sub != null && (
      <p className="text-xs text-ink-soft">
        {Number(sub).toLocaleString()} unique visitors
      </p>
    )}
  </div>
);

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [songData, setSongData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const [pagesRes, songsRes] = await Promise.all([
        api.get(`/admin/analytics?days=${d}`),
        api.get(`/admin/song-analytics?days=${d}`),
      ]);
      setData(pagesRes.data);
      setSongData(songsRes.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(days);
  }, [days]);

  const totals = data?.totals || {};
  const daily = data?.daily || [];
  const topPages = data?.top_pages || [];
  const topRefs = data?.top_referrers || [];

  const maxDaily = useMemo(
    () => Math.max(1, ...daily.map((d) => d.views || 0)),
    [daily]
  );

  return (
    <div className="bg-paper min-h-[80vh]" data-testid="admin-analytics-page">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-soft hover:text-terracotta mb-8"
          data-testid="admin-analytics-back-link"
        >
          <ArrowLeft size={14} /> Dashboard
        </Link>

        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-rule pb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
              Visitor Counter
            </p>
            <h1 className="font-serif-display text-3xl lg:text-4xl text-ink tracking-tight">
              Who&apos;s reading?
            </h1>
            <p className="text-sm text-ink-soft mt-2 max-w-xl">
              A private, anonymous tally of visits to your public pages. We don&apos;t
              store any personal data, cookies, or IP addresses — just counts.
            </p>
          </div>
          <div className="flex items-center gap-2" data-testid="admin-analytics-range">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                data-testid={`admin-analytics-range-${d}`}
                className={`px-3 py-2 text-[11px] tracking-[0.18em] uppercase border ${
                  days === d
                    ? "bg-ink text-paper border-ink"
                    : "border-rule text-ink-soft hover:text-ink"
                }`}
              >
                {d} days
              </button>
            ))}
          </div>
        </div>

        {loading && !data ? (
          <p className="text-ink-soft mt-10" data-testid="admin-analytics-loading">
            Loading…
          </p>
        ) : (
          <>
            {/* Stat cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
              <StatCard
                icon={Calendar}
                label="Today"
                value={totals.views_today}
                sub={totals.unique_today}
                testid="admin-stat-today"
              />
              <StatCard
                icon={TrendingUp}
                label="Last 7 days"
                value={totals.views_week}
                sub={totals.unique_week}
                testid="admin-stat-week"
              />
              <StatCard
                icon={TrendingUp}
                label="Last 30 days"
                value={totals.views_month}
                sub={totals.unique_month}
                testid="admin-stat-month"
              />
              <StatCard
                icon={Eye}
                label="All time"
                value={totals.views_all_time}
                sub={totals.unique_all_time}
                testid="admin-stat-alltime"
              />
            </div>

            {/* Chart */}
            <div className="mt-12 border border-rule p-6" data-testid="admin-analytics-chart">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif-display text-2xl text-ink">
                  Visits over the last {data?.days || days} days
                </h2>
                <p className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">
                  Daily page views
                </p>
              </div>
              <div className="flex items-end gap-1 h-48">
                {daily.map((d) => {
                  const h = Math.round(((d.views || 0) / maxDaily) * 100);
                  return (
                    <div
                      key={d.date}
                      className="flex-1 flex flex-col justify-end group relative"
                      title={`${d.date}: ${d.views} views, ${d.visitors} visitors`}
                    >
                      <div
                        className="bg-terracotta hover:bg-ink transition-colors min-h-[2px]"
                        style={{ height: `${h}%` }}
                      />
                      <span className="absolute -top-7 left-1/2 -translate-x-1/2 hidden group-hover:block whitespace-nowrap bg-ink text-paper text-[10px] px-2 py-1">
                        {d.views} on {d.date.slice(5)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-[10px] text-ink-soft mt-2 tracking-wide">
                <span>{daily[0]?.date}</span>
                <span>{daily[daily.length - 1]?.date}</span>
              </div>
            </div>

            {/* Top pages + referrers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-12">
              <div className="border border-rule p-6" data-testid="admin-top-pages">
                <div className="flex items-center gap-2 text-ink-soft mb-4">
                  <Eye size={16} />
                  <p className="text-[11px] uppercase tracking-[0.2em]">
                    Most-read pages (last 30 days)
                  </p>
                </div>
                {topPages.length === 0 ? (
                  <p className="text-ink-soft italic text-sm">No views yet — share your link!</p>
                ) : (
                  <ul className="divide-y divide-rule">
                    {topPages.map((p, i) => (
                      <li
                        key={p.path}
                        data-testid={`admin-top-page-${i}`}
                        className="py-3 flex items-center justify-between gap-4"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-serif-display text-base text-ink truncate">
                            {p.label || (p.path === "/" ? "Home" : p.path)}
                          </p>
                          <p className="text-[10px] text-ink-soft tracking-wide truncate">
                            {p.path}
                          </p>
                        </div>
                        <span className="text-xs text-ink-soft whitespace-nowrap">
                          {p.views.toLocaleString()} views ·{" "}
                          {p.visitors.toLocaleString()} visitors
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="border border-rule p-6" data-testid="admin-top-refs">
                <div className="flex items-center gap-2 text-ink-soft mb-4">
                  <Users size={16} />
                  <p className="text-[11px] uppercase tracking-[0.2em]">
                    Where readers came from (last 30 days)
                  </p>
                </div>
                {topRefs.length === 0 ? (
                  <p className="text-ink-soft italic text-sm">
                    No outside referrers yet. When people click a link from
                    Google, Facebook, etc., it will show up here.
                  </p>
                ) : (
                  <ul className="divide-y divide-rule">
                    {topRefs.map((r, i) => (
                      <li
                        key={r.referrer}
                        data-testid={`admin-top-ref-${i}`}
                        className="py-3 flex items-center justify-between gap-4"
                      >
                        <span className="text-sm text-ink truncate" title={r.referrer}>
                          {(() => {
                            try {
                              return new URL(r.referrer).hostname;
                            } catch {
                              return r.referrer;
                            }
                          })()}
                        </span>
                        <span className="text-xs text-ink-soft whitespace-nowrap">
                          {r.views.toLocaleString()} views
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Song plays & shares (Option S) */}
            <div className="border border-rule p-6 mt-6" data-testid="admin-song-plays">
              <div className="flex items-center gap-2 text-ink-soft mb-4">
                <Music size={16} />
                <p className="text-[11px] uppercase tracking-[0.2em]">
                  Song plays &amp; shares (last {songData?.days || days} days)
                </p>
              </div>
              {!songData || songData.songs.length === 0 ? (
                <p className="text-ink-soft italic text-sm">
                  No song data yet. Once visitors hit play on a song, it will show up here.
                </p>
              ) : (
                <ul className="divide-y divide-rule">
                  {songData.songs.map((s, i) => (
                    <li
                      key={s.id}
                      data-testid={`admin-song-plays-${i}`}
                      className="py-3 flex items-center justify-between gap-4"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-serif-display text-base text-ink truncate">
                          {s.title}
                        </p>
                        {!s.is_featured && (
                          <p className="text-[10px] text-ink-soft italic">Hidden</p>
                        )}
                      </div>
                      <div className="flex items-center gap-5 text-xs text-ink-soft whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5" title="Times someone hit Play">
                          <Music size={12} className="text-terracotta" />
                          <strong className="text-ink font-serif-display text-base">{s.plays.toLocaleString()}</strong> plays
                        </span>
                        <span className="inline-flex items-center gap-1.5" title="Unique listeners">
                          <Users size={12} />
                          {s.listeners.toLocaleString()} listeners
                        </span>
                        <span className="inline-flex items-center gap-1.5" title="Times song was shared via Share button">
                          <Share2 size={12} />
                          {s.shares.toLocaleString()} shares
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
